import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import HCaptcha from "@hcaptcha/react-hcaptcha";

// Context
import { UserContext } from "contexts/UserContext";

// Components
import { Flex } from "components/spocketUI/";

// Utils
import { checkEmailInput, checkPasswordInput } from "utils/validations";
import { signInUser } from "utils/api/user/requests";
import { analytics } from "utils/analytics";

// Routes
import paths from "routes/paths";

// Assets
import EyeIcon from "assets/images/icon-eye.svg";
import ErrorIcon from "assets/images/icon-error.svg";

// Actions
import {
  setupCredentialStorage,
  updateSettingsFromCredentials,
  signIn,
  register,
  verifyAuth
} from "utils/actions";

// Style
import {
  Container,
  Form,
  FormContainer,
  FormFieldContainer,
  StyledInput,
  StyledButton,
  StyledButtonMobile,
  PasswordErrorText,
  EmailErrorText,
  AuthErrorText,
  ForgotPasswordContainer,
  ForgotPasswordLink,
  ShowPasswordButton,
  ErrorIconContainer,
  Icon
} from "./SignInForm.style";

const SignInFormComponent = ({ setIsLoading, isFormActive, isPhone, onFocusForm }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { emailUsedOnSignup } = useContext(UserContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [authError, setAuthError] = useState("");
  const [validatedToken, setValidatedToken] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showPasswordIsActive, setShowPasswordIsActive] = useState(false);
  const hCaptchaKey = process.env.REACT_APP_HCAPTCHA_SITE_KEY;
  const captchaRef = useRef(null);

  const checkCaptcha = async event => {
    event.preventDefault();
    try {
      const captchaResponse = await captchaRef.current.execute({
        async: true
      });
      const responseToken = captchaResponse.response;
      setCaptchaToken(responseToken);
    } catch {
      setAuthError(t("Checkout.Error.Captcha"));
    }
  };

  const validate = () => {
    const emailError = checkEmailInput(email);
    const passError = checkPasswordInput(password);
    setEmailError(emailError);
    setPasswordError(passError);
    return emailError.length > 0 || passError.length > 0;
  };

  const handleSubmit = () => {
    if (captchaToken || validatedToken) {
      const hasErrors = validate();
      if (!hasErrors) {
        setIsLoading(true);
        signInUser({
          email,
          password,
          hCaptchaToken: captchaToken || validatedToken
        })
          .then(res => {
            const { store_credential: storeCredential } = res;
            const { storeId } = storeCredential;
            setupCredentialStorage(res);
            updateSettingsFromCredentials(res);
            signIn();
            register();
            verifyAuth();
            // Redirection logic
            const redirectDestination = localStorage.getItem("redirect_to_url_after_signin") || "/search";
            navigate(redirectDestination);
            localStorage.removeItem("redirect_to_url_after_signin");
            // Segment - Identify user when they sign up (Signup)
            analytics.identify(storeId, { source: "web" });
            // Segment - conversion (Signup)
            analytics.track("login_complete", { storeId: storeId, source: "web" });
          })
          .catch(error => {
            const { response = {} } = error;
            const { data } = response;
            if (data) {
              const { message } = data;
              setAuthError(message || t("Login.Error.Generic"));
            } else {
              setAuthError(t("Login.Error.Generic"));
            }
            setIsLoading(false);
          });
      }
    } else {
      setAuthError(t("Checkout.Error.Captcha"));
    }
  };

  const onClickPasswordButton = useCallback(
    event => {
      event.preventDefault();
      setShowPasswordIsActive(!showPasswordIsActive);
    },
    [showPasswordIsActive]
  );

  const handleKeyPress = event => {
    if (event.key === "Enter") {
      checkCaptcha(event);
    }
  };

  useEffect(() => {
    if (emailUsedOnSignup) {
      setEmail(emailUsedOnSignup);
      setEmailError(t("Login.Error.EmailExists"));
    }
  }, [emailUsedOnSignup]);

  useEffect(() => {
    if (captchaToken) handleSubmit();
  }, [captchaToken]);

  return (
    <Container id="signin-form">
      <Form onSubmit={checkCaptcha}>
        <FormContainer>
          <FormFieldContainer>
            <StyledInput
              name="email"
              type="text"
              hasError={emailError}
              placeholder="Email"
              data-cy="login-text"
              value={email}
              onKeyDown={handleKeyPress}
              onChange={val => {
                setEmail(val.target.value);
                setEmailError("");
                setAuthError("");
              }}
              onClick={isPhone ? () => onFocusForm() : null}
            />
            {emailError ? (
              <>
                <ErrorIconContainer type="email">
                  <Icon src={ErrorIcon} alt="error" />{" "}
                </ErrorIconContainer>
                <EmailErrorText>{emailError}</EmailErrorText>
              </>
            ) : null}
          </FormFieldContainer>
          {!isPhone || (isPhone && isFormActive) ? (
            <>
              <FormFieldContainer>
                <StyledInput
                  name="password"
                  type={showPasswordIsActive ? "text" : "password"}
                  hasError={passwordError}
                  placeholder="Password"
                  data-cy="login-password"
                  value={password}
                  onKeyDown={handleKeyPress}
                  onChange={val => {
                    setPassword(val.target.value);
                    setPasswordError("");
                    setAuthError("");
                  }}
                />
                {passwordError ? (
                  <>
                    <ErrorIconContainer type="password">
                      <Icon src={ErrorIcon} alt="error" />{" "}
                    </ErrorIconContainer>
                    <PasswordErrorText>{passwordError}</PasswordErrorText>{" "}
                  </>
                ) : (
                  <ShowPasswordButton
                    type="button"
                    onClick={onClickPasswordButton}
                    showPasswordIsActive={showPasswordIsActive}
                    title={t("Login.Button.ShowPassword")}
                  >
                    <Icon src={EyeIcon} alt="Show password" />
                  </ShowPasswordButton>
                )}
                {authError.length > 0 ? (
                  <AuthErrorText data-cy="login-error">{authError}</AuthErrorText>
                ) : null}
                <ForgotPasswordContainer>
                  <ForgotPasswordLink to={paths.auth.forgotPassword} data-heap="forgot-password-link">
                    {t("Login.ForgotLink.Label")}
                  </ForgotPasswordLink>
                </ForgotPasswordContainer>
              </FormFieldContainer>
              {isPhone ? (
                <StyledButtonMobile title={t("Login.Button.Title")} data-cy="login-button">
                  {t("Login.Button.Label")}
                </StyledButtonMobile>
              ) : (
                <StyledButton title={t("Login.Button.Title")} data-cy="sign-up-button">
                  {t("Login.Button.Label")}
                </StyledButton>
              )}
            </>
          ) : null}

          <Flex marginTop="15px" marginBottom="15px">
            <HCaptcha
              sitekey={hCaptchaKey}
              onVerify={token => {
                setValidatedToken(token);
              }}
              onError={() => {
                setAuthError(t("Checkout.Error.Captcha"));
              }}
              ref={captchaRef}
              size="invisible"
            />
          </Flex>
        </FormContainer>
      </Form>
    </Container>
  );
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ signIn, register, verifyAuth }, dispatch);
}

function mapStateToProps(state) {
  return {};
}

export const SignInForm = connect(mapStateToProps, mapDispatchToProps)(SignInFormComponent);

SignInForm.propTypes = {
  setIsLoading: PropTypes.func.isRequired,
  isFormActive: PropTypes.bool.isRequired,
  isPhone: PropTypes.bool,
  onFocusForm: PropTypes.func.isRequired
};
